// Share / Export for every list in the app — one PDF and one Excel builder that
// any page feeds with the columns it already renders on screen, so a report
// never needs its own exporter (the same idea as SharpAccounting's
// ReportShareDialog, rebuilt for this app's plain-CSS UI).
//
// A page passes:
//   title     "Guests"                      - the report's name
//   subtitle  "Sahil weds Diksha · Wedding" - event / filter line under it
//   columns   [{ key, label, align?, width?, render? }]
//   rows      the array it is already showing
//   totalsRow optional { [key]: value } printed as a bold last line
//
// A report may instead be SEVERAL blocks under one heading (the Brief Report):
//   sections  [{ title, note?, columns, rows, totalsRow? }]
//   summary   [{ label, value }] - the headline numbers, printed above them
// In the PDF each block is a table of its own under its heading; in Excel each
// block is a WORKSHEET of its own, with the summary as the first sheet.
//
// `render` may return JSX (the screen's chips and links): cellText() walks it
// down to plain text, so a page never has to keep a second set of columns.
//
// jsPDF / xlsx are ~1 MB together and most sessions never export, so both are
// loaded with import() the first time someone opens the dialog.

export const PDF_MIME = 'application/pdf';
export const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// ── A cell's plain text, whatever the column renders on screen ──────────────
const nodeText = (node) => {
  if (node === null || node === undefined || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (node.props) {
    if (node.props.children !== undefined) return nodeText(node.props.children);
    if (node.props.value !== undefined) return nodeText(node.props.value);
  }
  return '';
};

export const cellText = (col, row) => {
  if (col.exportValue) return String(col.exportValue(row) ?? '');
  const raw = col.render ? col.render(row) : row[col.key];
  const text = nodeText(raw);
  return text.replace(/\s+/g, ' ').trim();
};

// A column every row leaves empty is dropped: narrow paper, fewer columns.
const visibleColumns = (columns, rows) => (columns || []).filter(col => {
  if (col.exportHide) return false;
  if (!rows.length || col.keepEmpty) return true;
  return rows.some(row => cellText(col, row) !== '');
});

// One table, or the blocks of a multi-part report — the same shape either way.
const toBlocks = ({ columns, rows, totalsRow, sections }) => (
  Array.isArray(sections) && sections.length
    ? sections.map(s => ({ ...s, rows: s.rows || [], cols: visibleColumns(s.columns, s.rows || []) }))
    : [{ rows: rows || [], totalsRow, cols: visibleColumns(columns, rows || []) }]
);

export const safeFileName = (parts, ext) => {
  const base = parts.filter(Boolean).join(' ').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${base || 'Report'}.${ext}`;
};

// When the report was taken - the DATE AND THE TIME, because two printouts of
// the same list an hour apart are different lists.
const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const h = d.getHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${h12}:${p(d.getMinutes())} ${h < 12 ? 'AM' : 'PM'}`;
};

// ── PDF ─────────────────────────────────────────────────────────────────────
export async function buildReportPdf({
  title, subtitle, note, columns, rows, totalsRow, sections, summary,
  orientation = 'landscape', organiserName, eventName,
  density = 'compact',          // compact = more rows per page; normal = easier to read
}) {
  const d = density === 'normal'
    ? { font: 8, pad: 1.6, head: 8, margin: 12, titleGap: 5 }
    : { font: 6.6, pad: 0.8, head: 6.8, margin: 7, titleGap: 4 };
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new JsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const blocks = toBlocks({ columns, rows, totalsRow, sections });
  const rowCount = blocks.reduce((n, b) => n + b.rows.length, 0);

  // Header: who it belongs to, what the list is, when it was taken.
  // One header line instead of four: name of the event, what the list is and
  // its filters on the left, when it was taken and how many rows on the right.
  const left = [eventName || organiserName || 'Mehfil', title, subtitle].filter(Boolean).join('  ·  ');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(d.font + 2.4);
  doc.text(String(left), d.margin, 8);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(d.font); doc.setTextColor(90);
  doc.text(`${rowCount} ${rowCount === 1 ? 'row' : 'rows'}  ·  ${stamp()}`, pageW - d.margin, 8, { align: 'right' });
  let y = 8;
  if (note) {
    doc.setFont('helvetica', 'italic'); doc.setTextColor(120);
    doc.text(String(note), d.margin, y + 4);
    y += 4;
  }
  // The headline numbers of a brief: one line, so they are read before the tables.
  if (Array.isArray(summary) && summary.length) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(d.font + 0.8); doc.setTextColor(25);
    doc.text(summary.map(s => `${s.label}: ${s.value}`).join('    ·    '), d.margin, y + 4.6);
    y += 5;
  }
  doc.setTextColor(0);

  const usable = pageW - 2 * d.margin;

  // Columns with a pdfWidth (mm) keep their proportions; when they add up to
  // more than the page (portrait, many functions) they are all scaled down.
  const drawTable = (block, startY) => {
    const cols = block.cols;
    const fixed = cols.reduce((s, c) => s + (Number(c.pdfWidth) || 0), 0);
    const autoCount = cols.filter(c => !c.pdfWidth).length;
    const autoRoom = autoCount * 36;   // what each column without a width is left at least
    // All columns sized → stretch or shrink them to exactly the page width.
    const scale = fixed > 0 && autoCount === 0 ? usable / fixed
      : fixed > 0 && fixed > usable - autoRoom ? Math.max(usable - autoRoom, 10) / fixed : 1;

    const body = block.rows.map(row => cols.map(col => cellText(col, row)));
    if (block.totalsRow) body.push(cols.map(col => String(block.totalsRow[col.key] ?? '')));

    autoTable(doc, {
      startY,
      head: [cols.map(col => col.label)],
      body,
      theme: 'grid',
      // Dark, clearly visible grid lines - the light default disappears on a
      // printed page (and on a photocopy of it).
      styles: { font: 'helvetica', fontSize: d.font, cellPadding: d.pad, overflow: 'linebreak', textColor: 0, lineWidth: 0.25, lineColor: [40, 40, 40] },
      headStyles: { fillColor: [215, 225, 240], textColor: 0, fontStyle: 'bold', fontSize: d.head, lineWidth: 0.3, lineColor: [20, 20, 20] },
      alternateRowStyles: { fillColor: [249, 250, 252] },
      columnStyles: Object.fromEntries(cols.map((col, i) => [i, {
        halign: col.align === 'num' || col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left',
        cellWidth: col.pdfWidth ? Number(col.pdfWidth) * scale : 'auto',
      }])),
      // The totals line is the last body row: print it bold.
      didParseCell: (data) => {
        const isTotals = block.totalsRow && data.section === 'body' && data.row.index === body.length - 1;
        if (isTotals) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [238, 242, 248];
        }
        // A `tick` column: the PDF font has no ✓, so the cell is left blank
        // here and a tick is drawn into it in didDrawCell.
        const col = cols[data.column.index];
        if (col && col.tick && data.section === 'body' && !isTotals) {
          data.cell.tick = data.cell.raw === '✓';
          data.cell.text = [''];
        }
      },
      didDrawCell: (data) => {
        if (!data.cell.tick) return;
        const { x, y: cy0, width, height } = data.cell;
        const s = Math.min(height * 0.55, 3);
        const cx = x + width / 2, cy = cy0 + height / 2;
        doc.setDrawColor(0, 95, 40); doc.setLineWidth(0.55);
        doc.line(cx - s * 0.5, cy, cx - s * 0.15, cy + s * 0.38);
        doc.line(cx - s * 0.15, cy + s * 0.38, cx + s * 0.55, cy - s * 0.42);
        doc.setDrawColor(0); doc.setLineWidth(0.1);
      },
      didDrawPage: () => {
        const page = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(d.font - 0.6); doc.setTextColor(120);
        doc.text(`${title || 'Report'} — page ${page}`, pageW / 2, pageH - 3.5, { align: 'center' });
        doc.setTextColor(0);
      },
      margin: { left: d.margin, right: d.margin, top: 10, bottom: 7 },
    });
  };

  let cursor = y + d.titleGap;
  blocks.forEach((block, i) => {
    if (block.title) {
      // A heading is never left alone at the foot of a page.
      if (cursor > pageH - 28) { doc.addPage(); cursor = 12; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(d.font + 1.8); doc.setTextColor(20);
      doc.text(String(block.title), d.margin, cursor + 3.4);
      cursor += 4.6;
      if (block.note) {
        doc.setFont('helvetica', 'italic'); doc.setFontSize(d.font); doc.setTextColor(120);
        doc.text(String(block.note), d.margin, cursor + 3);
        cursor += 3.6;
      }
      doc.setTextColor(0);
      cursor += 1;
    }
    drawTable(block, cursor);
    cursor = doc.lastAutoTable.finalY + (i < blocks.length - 1 ? 7 : 0);
  });

  return doc.output('blob');
}

// ── Excel ───────────────────────────────────────────────────────────────────
// One worksheet out of one block of a report.
const blockSheet = (XLSX, block, head) => {
  const cols = block.cols;
  const width = Math.max(cols.length, 2);
  const pad = (first, second) => {
    const line = Array(width).fill('');
    line[0] = first;
    if (second !== undefined) line[1] = second;
    return line;
  };

  const aoa = [];
  head.forEach(line => aoa.push(pad(String(line))));
  if (block.title) aoa.push(pad(String(block.title)));
  if (block.note) aoa.push(pad(String(block.note)));
  aoa.push(Array(width).fill(''));

  const headerIndex = aoa.length;
  aoa.push(cols.map(col => col.label));
  block.rows.forEach(row => aoa.push(cols.map(col => {
    const text = cellText(col, row);
    // Keep numbers numeric so Excel can sum them; a mobile number stays text.
    if (col.align === 'num' && text && /^-?[\d,]+(\.\d+)?$/.test(text.replace(/[₹\s]/g, ''))) {
      return Number(text.replace(/[₹,\s]/g, ''));
    }
    return text;
  })));
  if (block.totalsRow) aoa.push(cols.map(col => block.totalsRow[col.key] ?? ''));

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map(col => ({
    wch: Math.min(Math.max(String(col.label).length + 4, col.excelWidth || 14), 42),
  }));
  // The title block spans the table so it reads like a report, not a stray cell.
  ws['!merges'] = Array.from({ length: Math.max(headerIndex - 1, 0) }, (_, r) => ({
    s: { r, c: 0 }, e: { r, c: Math.max(width - 1, 0) },
  }));
  ws['!freeze'] = { xSplit: 0, ySplit: headerIndex + 1 };
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerIndex, c: 0 },
      e: { r: headerIndex + block.rows.length, c: Math.max(width - 1, 0) },
    }),
  };
  return ws;
};

export async function buildReportExcel({
  title, subtitle, note, columns, rows, totalsRow, sections, summary, organiserName, eventName,
}) {
  const XLSX = await import('xlsx');
  const blocks = toBlocks({ columns, rows, totalsRow, sections });
  const rowCount = blocks.reduce((n, b) => n + b.rows.length, 0);

  const head = [String(eventName || organiserName || 'Mehfil')];
  if (organiserName && eventName) head.push(String(organiserName));
  head.push(String(title || 'Report'));
  if (subtitle) head.push(String(subtitle));
  if (note) head.push(String(note));
  head.push(`Printed: ${stamp()}   ·   ${rowCount} ${rowCount === 1 ? 'row' : 'rows'}`);

  const wb = XLSX.utils.book_new();

  // The headline numbers come first, on a sheet of their own, so the file
  // opens on the brief and the detail follows behind it.
  if (Array.isArray(summary) && summary.length) {
    const aoa = head.map(line => [String(line), '']);
    aoa.push(['', '']);
    summary.forEach(s => aoa.push([String(s.label), s.value]));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 34 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Brief');
  }

  const used = new Set();
  blocks.forEach((block, i) => {
    // Excel sheet names: 31 characters, unique, none of  : \ / ? * [ ]
    let name = String(block.title || title || 'Report').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 28) || `Sheet ${i + 1}`;
    while (used.has(name.toLowerCase())) name = `${name.slice(0, 26)} ${i + 1}`;
    used.add(name.toLowerCase());
    XLSX.utils.book_append_sheet(wb, blockSheet(XLSX, block, blocks.length > 1 ? [head[0], head[head.length - 1]] : head), name);
  });

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], { type: EXCEL_MIME });
}

// ── Where the file goes ─────────────────────────────────────────────────────
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Print: the PDF opens in a hidden frame and the browser's print dialog is
// asked for it, so what is printed is exactly what the PDF shows.
export function printPdfBlob(blob) {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  frame.src = url;
  frame.onload = () => {
    try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { window.open(url, '_blank'); }
  };
  document.body.appendChild(frame);
  setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 60000);
}
